/* eslint-disable react-hooks/exhaustive-deps */
import {
  fetchEnd,
  fetchError,
  fetchStart,
  fetchSuccess,
  setInitUrl,
} from "data/actions";
import { setAuthUser, userAuthSignOut } from "data/actions/Auth";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { httpClient } from "utils/Api";
import {
  ClientDetail,
  ClientRefreshToken,
  ClientSelectedProgram,
  ClientToken,
} from "utils/Constant";
import {
  getCookieValue,
  removeCookieValue,
  setCookieValue,
} from "utils/Storage";

export const useProvideAuth = () => {
  const [isLoadingUser, setLoadingUser] = useState(true);
  const dispatch = useDispatch();
  const history = useHistory();

  const userLogin = (user, callbackFun) => {
    const userData = {
      username: user.username,
      password: user.password,
    };
    dispatch(fetchStart());
    httpClient
      .post("/login/", userData)
      .then(({ data }) => {
        if (data?.token) {
          if (user.remember) {
            const farFutureDate = new Date();
            farFutureDate.setDate(farFutureDate.getDate() + 365 * 10);
            setCookieValue(ClientToken, data.token, {
              expires: farFutureDate,
            });
            setCookieValue(ClientRefreshToken, data?.refresh, {
              expires: farFutureDate,
            });
          } else {
            setCookieValue(ClientToken, data.token);
            setCookieValue(ClientRefreshToken, data?.refresh);
          }
          getAuthUser();
          dispatch(fetchSuccess("Successfully logged in"));
          if (callbackFun) callbackFun();
          return;
        }
        dispatch(fetchError(data.error));
      })
      .catch((error) => {
        const message = error.response?.data?.detail || error.message;
        dispatch(fetchError(message));
        clearUser();
      })
      .finally(() => {
        dispatch(fetchEnd());
      });
  };

  /**
   * @function getAuthUser
   * @description get authenticated user's data
   */
  const getAuthUser = () => {
    dispatch(fetchStart());
    return httpClient
      .get("/users/me/")
      .then(({ data }) => {
        if (data) {
          dispatch(fetchSuccess());
          setUser(data);
          return data;
        }
        dispatch(fetchError(data.error));
        return Promise.reject(new Error(data.error || "No user data"));
      })
      .catch((error) => {
        clearUser();
        const message =
          error.response?.data?.detail || error.message || error[1]?.message;
        dispatch(fetchError(message));
        return Promise.reject(error);
      });
  };

  const sendPasswordResetEmail = (email, callbackFun) => {
    dispatch(fetchStart());

    setTimeout(() => {
      dispatch(fetchSuccess());
      if (callbackFun) callbackFun();
    }, 300);
  };

  const confirmPasswordReset = (user, callbackFun) => {
    const userData = {
      username: user.username,
      password: user.password,
    };
    dispatch(fetchStart());
    try {
      httpClient.post("users/change_password/", userData);
      setTimeout(() => {
        dispatch(fetchSuccess("Operation Successful"));
        if (callbackFun) callbackFun();
      }, 300);
    } catch (error) {
      fetchError(error.message);
    } finally {
      dispatch(fetchEnd());
    }
  };

  const renderSocialMediaLogin = () => null;

  /**
   * @function userSignOut
   * @description Sign out user
   */
  const userSignOut = async () => {
    dispatch(fetchStart());

    try {
      const _refreshToken = getCookieValue(ClientRefreshToken);
      if (_refreshToken) {
        try {
          await httpClient.post("users/logout/", {
            refresh: getCookieValue(ClientRefreshToken),
          });
        } catch (logoutError) {
          // Silently ignore logout endpoint errors (invalid/expired tokens, network issues)
          // User will still be logged out locally
        }
      } // Only call logout endpoint if refresh token exists
      await clearUser();
      dispatch(fetchSuccess("Operation Success"));
      history.push("/signin");
    } catch (error) {
      // If clearUser fails, silently clear client state and redirect.
      try {
        await clearUser();
      } catch (e) {
        // ignore
      }
      history.push("/signin");
    } finally {
      dispatch(fetchEnd());
    }
  };

  /**
   * @function clearUser
   * @description Clear user data from local storage and cookies
   */
  const clearUser = async () => {
    removeCookieValue(ClientToken);
    removeCookieValue(ClientDetail);
    removeCookieValue(ClientSelectedProgram);
    removeCookieValue(ClientRefreshToken);
    setLoadingUser(false);
    fetchError("");
    dispatch(userAuthSignOut());
    dispatch(setInitUrl(""));
  };

  /**
   * @function setUser
   * @description set user data in local storage and cookies
   * @param {object} user
   */
  const setUser = (user) => {
    setCookieValue(ClientDetail, JSON.stringify(user));
    dispatch(setAuthUser(user));
  };

  useEffect(() => {
    const initializeUser = async () => {
      const token = getCookieValue(ClientToken);
      const _user = getCookieValue(ClientDetail);
      const userData = _user ? JSON.parse(_user) : null;

      if (!token) {
        clearUser();
        setLoadingUser(false);
        return;
      }

      if (userData?.id) {
        dispatch(setAuthUser(userData));
      }

      // Always refresh user from server to get the latest record on page reload
      try {
        await getAuthUser();
      } catch (e) {
        // getAuthUser already handles clearing user and dispatching errors
      } finally {
        setLoadingUser(false);
      }
    };

    initializeUser();
  }, [dispatch]);

  // Return the user object and auth methods
  return {
    isLoadingUser,
    userLogin,
    userSignOut,
    renderSocialMediaLogin,
    sendPasswordResetEmail,
    confirmPasswordReset,
  };
};
